import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "@/lib/api";
import { useProject } from "@/lib/project";
import { keys } from "@/lib/queries";
import type { Color, Finance, FinanceCurrency, MemberWeight, Payment } from "@/lib/types";
import { cn, formatDay, formatMoney, parseDay, parseMoney, pluralize, todayISO } from "@/lib/utils";

/** Runs a finance change, reports failures as a toast and refreshes the finance data. Resolves to whether it succeeded. */
export function useFinanceRun() {
  const { pid } = useProject();
  const qc = useQueryClient();
  return async (p: Promise<unknown>): Promise<boolean> => {
    try {
      await p;
      return true;
    } catch (err) {
      window.dispatchEvent(new CustomEvent("app:error", { detail: err }));
      return false;
    } finally {
      await qc.invalidateQueries({ queryKey: keys.finance(pid) });
    }
  };
}

function daysBetween(from: string, to: string) {
  return Math.round((parseDay(to).getTime() - parseDay(from).getTime()) / 86_400_000);
}

export type PaymentStatusKey = "paid" | "partial" | "overdue" | "due_today" | "due_soon" | "upcoming" | "unscheduled";

export interface PaymentStatus {
  key: PaymentStatusKey;
  label: string;
  color: Color;
  detail: string;
}

/** Status of a scheduled client payment relative to today. `currency` is used to describe partial payments. */
export function paymentStatus(p: Payment, currency: string, today = todayISO()): PaymentStatus {
  const last = p.receipts[p.receipts.length - 1]?.receivedOn;
  if (p.received >= p.amount && last) {
    const late = p.dueDate ? daysBetween(p.dueDate, last) : 0;
    return {
      key: "paid",
      label: "Paid",
      color: "green",
      detail: `${p.receipts.length > 1 ? "Fully received" : "Received"} ${formatDay(last, true)}${late > 0 ? ` · ${pluralize(late, "day")} late` : ""}`,
    };
  }
  const days = p.dueDate ? daysBetween(today, p.dueDate) : null;
  const due =
    days === null
      ? "no due date"
      : days < 0
        ? `was due ${formatDay(p.dueDate, true)} · ${pluralize(-days, "day")} ago`
        : days === 0
          ? "due today"
          : `due ${formatDay(p.dueDate, true)} · in ${pluralize(days, "day")}`;
  if (p.received > 0) {
    return {
      key: "partial",
      label: "Partially paid",
      color: days !== null && days < 0 ? "red" : "purple",
      detail: `${formatMoney(p.amount - p.received, currency)} left · ${due}`,
    };
  }
  const cap = due.charAt(0).toUpperCase() + due.slice(1);
  if (days === null) return { key: "unscheduled", label: "No due date", color: "gray", detail: "Not scheduled yet" };
  if (days < 0) return { key: "overdue", label: "Overdue", color: "red", detail: cap };
  if (days === 0) return { key: "due_today", label: "Due today", color: "orange", detail: cap };
  if (days <= 7) return { key: "due_soon", label: "Due soon", color: "yellow", detail: cap };
  return { key: "upcoming", label: "Upcoming", color: "blue", detail: cap };
}

/** A payment that isn't fully received and whose due date has passed. */
export function isOverdue(p: Payment, today = todayISO()) {
  return p.received < p.amount && !!p.dueDate && p.dueDate < today;
}

export function StatusBadge({ status }: { status: PaymentStatus }) {
  return (
    <span className={cn("tag rounded-full ps-1.5 pe-2", `tag-${status.color}`)}>
      <span className={cn("me-1.5 inline-block size-2 shrink-0 rounded-full", `dot-${status.color}`)} />
      {status.label}
    </span>
  );
}

/** Older expenses (recorded before expenses were charged to balances) still waiting to be taken out of a payment. */
export function uncoveredExpenses(fin: Finance) {
  return fin.expenses.reduce((n, e) => n + (e.funding === "receipts" ? Math.max(0, e.amount - e.covered) : 0), 0);
}

/** Expense money nobody could cover yet; members repay it from their next payments. */
export function unfundedExpenses(fin: Finance) {
  return fin.debts.reduce((n, d) => n + (d.creditor === null ? d.outstanding : 0), 0);
}

/** Mirrors the server's largest-remainder split so previews match what gets locked. */
export function splitAmount(amount: number, members: MemberWeight[]) {
  const parts = members.map((m) => Math.floor(amount * m.share));
  let given = parts.reduce((a, b) => a + b, 0);
  const order = members
    .map((m, i) => ({ i, r: amount * m.share - Math.floor(amount * m.share) }))
    .sort((a, b) => b.r - a.r);
  for (let k = 0; given < amount && k < order.length; k++) {
    if (members[order[k].i].share > 0) {
      parts[order[k].i]++;
      given++;
    }
  }
  return parts;
}

export function Stat({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: ReactNode; tone?: "danger" | "muted" }) {
  return (
    <div className="min-w-0 rounded-lg border border-line p-3.5">
      <div className="truncate text-[13px] text-muted">{label}</div>
      <div className={cn("mt-1 truncate text-xl font-semibold tabular-nums", tone === "danger" && "text-danger", tone === "muted" && "text-muted")}>{value}</div>
      {hint && <div className="mt-1 text-xs text-faint">{hint}</div>}
    </div>
  );
}

/** Amount field showing the project currency; the value is the raw text so partial input like "12." is kept. */
export function MoneyInput({
  value,
  onChange,
  currency,
  autoFocus,
  invalid,
}: {
  value: string;
  onChange: (v: string) => void;
  currency: string;
  autoFocus?: boolean;
  invalid?: boolean;
}) {
  return (
    <div>
      <div className="relative">
      <input
        value={value}
        autoFocus={autoFocus}
        inputMode="decimal"
        placeholder="0.00"
        onChange={(e) => onChange(e.target.value)}
        className={cn("field pe-14 tabular-nums", invalid && "border-danger")}
        dir="ltr"
      />
      <span className="pointer-events-none absolute end-2.5 top-1/2 -translate-y-1/2 text-xs text-faint">{currency}</span>
      </div>
      <Equivalents value={parseMoney(value)} currency={currency} />
    </div>
  );
}

/** Dimmed equivalents of a typed amount, shown under money fields while typing. */
function Equivalents({ value, currency }: { value: number | null; currency: string }) {
  const { main, shown } = useContext(CurrencyCtx);
  if (!value || currency !== main || shown.length === 0) return null;
  return (
    <span className="mt-1 block text-xs text-faint tabular-nums" dir="ltr">
      {shown.map((c) => `≈ ${formatMoney(Math.round(value * c.rate), c.code)}`).join(" · ")}
    </span>
  );
}

/** Small numeric field for multipliers and weights; saves on blur or Enter when the value changed. */
export function MultiplierInput({ value, onSave, label }: { value: number; onSave: (v: number) => void; label: string }) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  const commit = () => {
    const v = Number(draft);
    if (draft.trim() === "" || !Number.isFinite(v) || v < 0 || v > 100) {
      setDraft(String(value));
      return;
    }
    if (v !== value) onSave(v);
  };
  return (
    <label className="flex items-center gap-1 text-sm text-muted">
      <span aria-hidden>×</span>
      <input
        value={draft}
        inputMode="decimal"
        aria-label={label}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
        className="h-8 w-16 rounded-md border border-line bg-transparent px-2 text-end text-sm text-fg tabular-nums outline-none focus:border-accent"
        dir="ltr"
      />
    </label>
  );
}

interface CurrencyDisplay {
  main: string;
  /** extra currencies whose equivalents are shown */
  shown: FinanceCurrency[];
}

const CurrencyCtx = createContext<CurrencyDisplay>({ main: "USD", shown: [] });

export function CurrencyProvider({ fin, children }: { fin: Finance; children: ReactNode }) {
  const value = { main: fin.settings.currency, shown: fin.currencies.filter((c) => c.visible) };
  return <CurrencyCtx.Provider value={value}>{children}</CurrencyCtx.Provider>;
}

/**
 * An amount in the main currency (in cents), followed by dimmer equivalents in the currencies set to show.
 * `stack` puts each equivalent on its own line; `inline` keeps them on the same line.
 */
export function Money({ value, layout = "stack", prefix = "", className }: { value: number; layout?: "stack" | "inline"; prefix?: string; className?: string }) {
  const { main, shown } = useContext(CurrencyCtx);
  const main_ = `${prefix}${formatMoney(value, main)}`;
  if (shown.length === 0) return <span className={cn("tabular-nums", className)}>{main_}</span>;
  const others = shown.map((c) => ({ code: c.code, text: `≈ ${prefix}${formatMoney(Math.round(value * c.rate), c.code)}` }));
  if (layout === "inline") {
    return (
      <span className={cn("tabular-nums", className)}>
        {main_}
        <span className="ms-1.5 text-[0.85em] font-normal text-faint">{others.map((o) => o.text).join(" · ")}</span>
      </span>
    );
  }
  return (
    <span className={cn("inline-block tabular-nums", className)}>
      <span className="block">{main_}</span>
      {others.map((o) => (
        <span key={o.code} className="block text-[0.75em] leading-snug font-normal text-faint">
          {o.text}
        </span>
      ))}
    </span>
  );
}

/** Quick switches to show or hide each extra currency's equivalents. */
export function CurrencyToggles({ fin }: { fin: Finance }) {
  const { pid } = useProject();
  const run = useFinanceRun();
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[13px]">
      <span className="text-muted">Also show in</span>
      {fin.currencies.map((c) => (
        <button
          key={c.id}
          type="button"
          aria-pressed={c.visible}
          onClick={() => run(api.updateCurrency(pid, c.id, { visible: !c.visible }))}
          title={`1 ${fin.settings.currency} = ${c.rate} ${c.code}`}
          className={cn("h-6 rounded-full border px-2 font-mono", c.visible ? "border-fg text-fg" : "border-line text-faint line-through hover:text-muted")}
        >
          {c.code}
        </button>
      ))}
    </div>
  );
}
